import MailIcon from "@redotech/redo-icons/mail-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Footer } from "./footer";
import { Header } from "./header";
import * as loginStyles from "./login.module.css";
import { SocialLoginOptionsSection } from "./social-login-options-section";
import { useEmailLink } from "./use-email-link";

export function EmailLink({
  onBack,
  title,
  nextUrl,
  showFooter = true,
}: {
  onBack?: () => void;
  title?: string;
  nextUrl?: string;
  showFooter?: boolean;
}) {
  const {
    texts,
    emailInput,
    onEmailChange,
    onSendLink,
    sendLinkDisabled,
    emailValidationError,
    resendEmailError,
    sendEmailError,
    showEmailConfirmationPage,
    canResend,
    onResendLink,
    socialLoginOptions,
  } = useEmailLink({ nextUrl, title });
  const errorUI =
    emailValidationError || sendEmailError || resendEmailError ? (
      <Flex align="center" flexDirection="column">
        {emailValidationError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {emailValidationError}
          </Text>
        )}
        {sendEmailError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {sendEmailError}
          </Text>
        )}
        {resendEmailError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {resendEmailError}
          </Text>
        )}
      </Flex>
    ) : null;

  const emailConfirmationUI = (
    <Flex className={loginStyles.cardContent} flexDirection="column" gap="3xl">
      <RedoFeaturedIcon color="black" Icon={MailIcon} size="xl" />
      <Flex flexDirection="column" gap="lg" maxW="xs">
        <Text fontSize="xl" fontWeight="semibold">
          {texts.emailConfirmationTitle}
        </Text>
        <Text fontSize="sm">{texts.emailConfirmationSubtitle}</Text>
      </Flex>
      <Flex align="center" flexDirection="row" gap="sm">
        <Text fontSize="sm">{texts.didNotReceiveEmail}</Text>
        <RedoButton
          disabled={!canResend}
          hierarchy={canResend ? "link_underline" : "link"}
          onClick={() => onResendLink()}
          text={texts.resendLink}
        />
      </Flex>
      {errorUI}
    </Flex>
  );

  const sendEmailUI = (
    <Flex className={loginStyles.cardContent} flexDirection="column" gap="3xl">
      <Header
        leftAlign
        logo={undefined}
        merchantName={texts.merchantName}
        onBack={onBack}
        subtitle={texts.subtitle}
        title={texts.title}
      />
      <Flex
        align="stretch"
        flex={1}
        flexDirection="column"
        gap="2xl"
        grow={1}
        justify="flex-start"
      >
        <RedoTextInput
          fullWidth
          label={texts.emailLabel}
          placeholder={texts.emailHint}
          required
          setValue={onEmailChange}
          size="md"
          value={emailInput}
        />
        <RedoButton
          disabled={sendLinkDisabled}
          hierarchy="primary"
          onClick={() => onSendLink()}
          size="lg"
          text={texts.sendLink}
          theme="normal"
        />
        {socialLoginOptions && socialLoginOptions.length > 0 && (
          <SocialLoginOptionsSection
            nextUrl={nextUrl}
            socialLoginOptions={socialLoginOptions}
          />
        )}
        {errorUI}
      </Flex>
      {showFooter && <Footer centered />}
    </Flex>
  );

  return showEmailConfirmationPage ? emailConfirmationUI : sendEmailUI;
}
